import { useEffect, useRef } from "react";
import { LanderConfigAction, useLanderConfig } from "../commons/ConfigContext";
import { sendAuctionExposureEvent } from "../commons/LanderEvent";
import { useAuctionRedirectExperiment } from "./useAuctionRedirectExperiment";

// PARKING-5212 side effects for the on-lander arms (control + treatment_b).
// Mounted at the parkweb layout entry points, so reaching here means every
// LanderRouter guard passed (treatment_c/none no-op; treatment_c owns its
// redirect in AuctionDetailsRedirect).
export function useAuctionArmEffects(): void {
  const { landerConfig, landerConfigDispatch } = useLanderConfig();
  const { variant, ddUrl } = useAuctionRedirectExperiment();

  const isOnLanderArm = variant === "control" || variant === "treatmentB";
  const inTreatmentCohort = variant === "treatmentB";
  const currentLink = landerConfig.lander?.banner?.link;

  // treatment_b: repoint the banner CTA at the Auctions DD page. The
  // link-equality guard keeps it idempotent against the reducer's deep-clone
  // re-run; the override lands post-effect, so first paint shows the SERP
  // link (valid fallback).
  useEffect(() => {
    if (variant !== "treatmentB" || !ddUrl || currentLink === ddUrl) return;
    landerConfigDispatch({
      type: LanderConfigAction.SET_BANNER_LINK,
      payload: { link: ddUrl },
    });
  }, [variant, ddUrl, currentLink, landerConfigDispatch]);

  // Channel: tag the adSense channel with the cohort so ad revenue is
  // attributable per arm. Both control and treatment_b render ads.
  const channelUpdatedRef = useRef(false);
  useEffect(() => {
    if (!isOnLanderArm || channelUpdatedRef.current) return;
    channelUpdatedRef.current = true;
    landerConfigDispatch({
      type: LanderConfigAction.UPDATE_CHANNEL_BY_TREATMENT,
      payload: { inTreatmentCohort },
    });
  }, [isOnLanderArm, inTreatmentCohort, landerConfigDispatch]);

  // Exposure: one AUCTION_EXPOSURE per assigned on-lander session (control +
  // treatment_b), fire-once via a ref (mirrors IntentExposureEvent). Fires
  // regardless of ddUrlError, since it is still an assignment.
  const exposedRef = useRef(false);
  useEffect(() => {
    if (!isOnLanderArm || exposedRef.current) return;
    exposedRef.current = true;
    sendAuctionExposureEvent(landerConfig);
  }, [isOnLanderArm, landerConfig]);
}
